/**
 * @file IndexNowKeysList.tsx
 * @description «Адреса сайта и ключи (N)» в карточке «Сообщать поисковикам
 * об изменениях» (phase19.16, партия «SEO-техника», v0.19.65; SEO8; макет
 * uiux-seo-tech.md §3.2): по умолчанию свёрнуто; у каждого адреса магазина
 * свой ключ — коротко (первые 6 и последние 4 знака), «Открыть файл ключа ↗»
 * (`https://<адрес>/<ключ>.txt`) и «Скопировать адрес». Кнопки переносятся
 * под адрес на 360, высота 48 px.
 */

import type { IndexNowKeyDto } from "@chaiberi/shared";
import { ChevronDown, Copy, ExternalLink } from "lucide-react";
import { useId, useState } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { keyRoleText, shortKey } from "../../../lib/site-seo/indexnow-view";
import { cn } from "@/lib/utils";

export function IndexNowKeysList({ keys }: { keys: IndexNowKeyDto[] }) {
  const notify = useNotify();
  const listId = useId();
  const [open, setOpen] = useState(false);
  if (keys.length === 0) return null;

  function copy(url: string) {
    // Буфер обмена недоступен (не HTTPS, старый браузер) — сказать, а не молчать.
    if (!navigator.clipboard) {
      notify.error("Не удалось скопировать — выделите адрес вручную.");
      return;
    }
    void navigator.clipboard
      .writeText(url)
      .then(() => notify.success("Адрес файла ключа скопирован"))
      .catch(() => notify.error("Не удалось скопировать — выделите адрес вручную."));
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Button variant="ghost" className="-mx-2 justify-between px-2" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((v) => !v)}>
        Адреса сайта и ключи ({keys.length})
        <ChevronDown aria-hidden className={cn("transition-transform", open && "rotate-180")} />
      </Button>
      {open ? (
        <ul id={listId} className="flex flex-col divide-y divide-[var(--outline-variant)] rounded-xl bg-surface-low">
          {keys.map((k) => (
            <li key={k.host} className="flex min-w-0 flex-col gap-1 p-3">
              <span className="type-body break-all text-on-surface">
                <span className="font-medium">{k.host}</span> · {keyRoleText(k)}
              </span>
              <span className="type-caption font-mono text-on-surface-variant" title={k.key}>
                ключ {shortKey(k.key)}
              </span>
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" className="max-sm:w-full">
                  <a href={k.fileUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink aria-hidden />
                    Открыть файл ключа ↗
                  </a>
                </Button>
                <Button variant="outline" className="max-sm:w-full" onClick={() => copy(k.fileUrl)}>
                  <Copy aria-hidden />
                  Скопировать адрес
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
